import React from 'react';
import PropTypes from 'prop-types';
import { dynastyTheme } from '../../../../../services/colorService';
import { formatBattingAvg, formatERA } from '../../../../../services/statFormatUtils';

const C = dynastyTheme.tokens.colors;

/**
 * StatsGrid Component
 * 
 * Responsive grid of stat cards. Used in Overview tabs to display season and rolling stats.
 * Supports 2/4/6 column layouts with dynamic stat categories.
 * 
 * @param {object} stats - Stats object (FLAT structure from PlayerOverviewTab)
 * @param {array} categories - Array of stat keys to display
 * @param {string} layout - Layout mode: "grid" | "row"
 * @param {boolean} showLabels - Whether to show stat labels (default: true)
 * @param {boolean} showTrends - Whether to show trend indicators (default: false)
 */
const StatsGrid = ({
stats,
categories,
layout = 'grid',
showLabels = true,
showTrends = false,
  isPitcher = false  // ADD THIS PROP
}) => {
  // Stat label mapping
  const statLabels = {
    // Hitting
    games_played: 'Games',
    games: 'Games',
    at_bats: 'AB',
    batting_avg: 'AVG',
    home_runs: 'HR',
    rbi: 'RBI',
    runs: 'Runs',
    stolen_bases: 'SB',
    obp: 'OBP',
    slg: 'SLG',
    ops: 'OPS',
    hits: 'Hits',
    doubles: '2B',
    triples: '3B',
    walks: 'BB',
    strikeouts: 'K',
    
    // Pitching
    innings_pitched: 'IP',
    era: 'ERA',
    whip: 'WHIP',
    wins: 'W',
    losses: 'L',
    saves: 'SV',
    strikeouts_pitched: 'K',
    hits_allowed: 'H',
    walks_allowed: 'BB',
    quality_starts: 'QS',
    earned_runs: 'ER',
    games_started: 'GS'
  };

  // Format stat value for display
  const formatStatValue = (key, value) => {
    if (value === null || value === undefined) return '--';

    // Rate stats: AVG, OBP, SLG, OPS, WHIP — 3 decimals, no leading zero
    if (['batting_avg', 'whip', 'obp', 'slg', 'ops'].includes(key)) {
      return formatBattingAvg(value);
    }

    // ERA — 2 decimals, keeps leading digits
    if (key === 'era') {
      return formatERA(value);
    }

    // Innings pitched - 1 decimal place
    if (key === 'innings_pitched') {
      return Number(value).toFixed(1);
    }

    // Default - no decimals
    return Math.round(value).toString();
  };

  // Get stat card
  const getStatCard = (category) => {
    // CANONICAL: Access nested hitting/pitching stats
    let value;
    if (stats.hitting || stats.pitching) {
      // Nested structure: stats = { hitting: {...}, pitching: {...} }
      value = isPitcher ? stats.pitching?.[category] : stats.hitting?.[category];
    } else {
      // Flat structure (fallback)
      value = stats[category];
    }
    
    const label = statLabels[category] || category.toUpperCase();
    const formattedValue = formatStatValue(category, value);

    return (
      <div key={category} style={cardStyle}>
        <div style={valueStyle}>{formattedValue}</div>
        {showLabels && <div style={labelStyle}>{label}</div>}
      </div>
    );
  };

  // Layout styles
  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: layout === 'row' 
      ? `repeat(${categories.length}, 1fr)` 
      : 'repeat(auto-fit, minmax(80px, 1fr))',
    gap: '8px',
    width: '100%'
  };

  const cardStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '12px 8px',
    backgroundColor: C.surface[855], // dynastyTheme.colors.darkLighter
    borderRadius: '8px',
    minHeight: '60px',
    transition: 'transform 100ms ease',
    cursor: 'default'
  };

  const valueStyle = {
    fontSize: '24px',
    fontWeight: '700',
    color: C.white,
    lineHeight: 1,
    marginBottom: showLabels ? '4px' : 0
  };

  const labelStyle = {
    fontSize: '12px',
    fontWeight: '500',
    color: C.coolGray[400], // dynastyTheme.colors.gray
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    lineHeight: 1
  };

  return (
    <div style={gridStyle}>
      {categories.map(category => getStatCard(category))}
    </div>
  );
};

StatsGrid.propTypes = {
  stats: PropTypes.object.isRequired,
  categories: PropTypes.arrayOf(PropTypes.string).isRequired,
  layout: PropTypes.oneOf(['grid', 'row']),
  showLabels: PropTypes.bool,
  showTrends: PropTypes.bool,
  isPitcher: PropTypes.bool  // ADD THIS
};

export default StatsGrid;
